import { intersection } from 'lodash';
import React from 'react';
import { BasicButton, IBasicButtonProps } from 'src/design/atoms/BasicButton';
import { useAuth } from 'src/hooks/useAuth';

export interface IAuthorizedButtonProps extends IBasicButtonProps {
  allowedRoles?: number[];
  allowedUsers?: number[];
  blockedUsers?: number[];
  allowOwner?: boolean;
  isOwner?: boolean;
}

/** @registry Renders a BasicButton only if the current user matches allowedRoles/allowedUsers/isOwner and isn't in blockedUsers (lenient: empty lists mean unrestricted); returns null when unauthorized. */
export const AuthorizedButton = React.forwardRef<
  HTMLButtonElement,
  IAuthorizedButtonProps
>(
  (
    {
      allowedRoles = [],
      allowOwner,
      isOwner,
      allowedUsers = [],
      blockedUsers = [],
      children,
      ...restProps
    },
    ref,
  ) => {
    const { isAdmin, user, roleIds } = useAuth();

    const isExcluded =
      blockedUsers.length > 0 &&
      blockedUsers.indexOf(user?.id || -1) >= 0 &&
      !isAdmin;
    const hasRole =
      allowedRoles.length == 0 ||
      (allowedRoles.length > 0 &&
        intersection(allowedRoles, roleIds).length > 0) ||
      isAdmin;
    const isUser =
      allowedUsers.length == 0 ||
      (allowedUsers.length > 0 && allowedUsers.indexOf(user?.id || -1) >= 0);
    const isAuthorizedOwner = allowOwner && isOwner;

    if (isExcluded) return null;
    if (!hasRole && !isUser && !isAuthorizedOwner) return null;

    return (
      <BasicButton {...restProps} ref={ref}>
        {children}
      </BasicButton>
    );
  },
);
AuthorizedButton.displayName = 'AuthorizedButton';
